/**
 * Ход года (core-loop-v2 §2; план 2d, Task 5): чистые помощники стора и
 * экрана без сигналов и DOM.
 *
 * Фазы: `morning` — сводка прошлого года и выбор фокуса; `actions` —
 * энергия тратится на места; `decision` — то же, но решение или мини-игра
 * ждут ответа (кнопка «Следующий год» закрыта); `ending` — идёт тик.
 *
 * Дельты года считаются между жизнью на утро прошлого года и жизнью на
 * утро нового: в них входят и действия игрока, и тик движка. Числа
 * округляются до целых — так же, как показывает `Stats`.
 */
import { gamblingLaw } from '@life/data';
import {
  CONDITION_CHRONIC,
  ENERGY_BASE,
  ENERGY_LOW_HEALTH,
  ENERGY_OLD_AGE,
  ENERGY_SCHOOL_MAX_AGE,
  ENERGY_TODDLER_MAX_AGE,
  clampStat,
  type Life,
  type LogEntry,
  type Stats,
} from '@life/engine';
import type { PlaceOptions } from '@life/minigames';
import type { Settings } from './settings';

export type Phase = 'morning' | 'actions' | 'decision' | 'ending';

export interface YearDeltas {
  /** Возраст прожитого года (тот, чьи записи и дельты показаны). */
  age: number;
  stats: Stats;
  money: number;
}

export const STAT_KEYS: readonly (keyof Stats)[] = ['health', 'happiness', 'knowledge', 'relations'];

function shown(v: number): number {
  return Math.round(clampStat(v));
}

/** Дельты между утром прошлого года (`before`) и утром нового (`after`). */
export function yearDeltas(before: Life, after: Life): YearDeltas {
  const stats = {} as Stats;
  for (const k of STAT_KEYS) stats[k] = shown(after.stats[k]) - shown(before.stats[k]);
  return { age: before.age, stats, money: Math.round(after.money) - Math.round(before.money) };
}

/** Записи прошедшего года: у живой жизни — возраст `age − 1`, у новорождённого — записи рождения. */
export function lastYearEntries(life: Life): LogEntry[] {
  const age = life.alive && life.age > 0 ? life.age - 1 : life.age;
  return life.log.filter((e) => e.age === age);
}

/** Почему энергии меньше базы: ключи `ui.energy.reason.*` по модификаторам `energyFor`. */
export function energyReasons(life: Life): string[] {
  const reasons: string[] = [];
  if (life.stats.health < ENERGY_LOW_HEALTH) reasons.push('ui.energy.reason.health');
  if (life.age >= ENERGY_OLD_AGE) reasons.push('ui.energy.reason.age');
  if (life.inSchool && life.age < ENERGY_SCHOOL_MAX_AGE) reasons.push('ui.energy.reason.school');
  if (life.childrenAges.some((a) => a <= ENERGY_TODDLER_MAX_AGE)) reasons.push('ui.energy.reason.toddler');
  if (life.conditions.includes(CONDITION_CHRONIC)) reasons.push('ui.energy.reason.chronic');
  return reasons;
}

export const ENERGY_MAX = ENERGY_BASE;

/** Условия мест: закон об азартных играх страны жизни и настройка учебного режима. */
export function placeOptions(life: Life, settings: Pick<Settings, 'gamblingEnabled'>): PlaceOptions {
  return { law: gamblingLaw(life.iso3).law, gamblingEnabled: settings.gamblingEnabled };
}
